import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
} from "@mui/material";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import StageDialog from "src/components/stage-admin/StageDialog";
import { del, get } from "src/utils/networkUtils";
import { getUserPermissions } from "src/utils/permissionsUtils";
import ConnectionMigrateDialog from "src/components/migrate/ConnectionMigrateDialog";
import useWithUrlTenant from "src/hooks/useTenant";

export default function MarketplacePacksPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [rows, setRows] = useState(null);
  const [openStageDialog, setOpenStageDialog] = useState(false);
  const [stage, setStage] = useState(null);
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const { isViewer } = getUserPermissions();
  const withTenant = useWithUrlTenant();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    get(withTenant(`/admin/marketplace/scoreable-packs`)).then(function (response) {
      setRows(response.packs);
    });
  }, [withTenant]);

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const handleCloseStageDialog = () => {
    setStage(null);
    setOpenStageDialog(false);
  };
  const handleDeletePack = (packId) => {
    setOpen(null);
    setOpenId(null);
    setLoading(true);
    del(`/admin/marketplace/scoreable-packs/${packId}`).then(function (
      response
    ) {
      window.location.reload(true);
    });
  };
  const handleSuccessStageDialog = () => {
    window.location.reload(true);
  };

  return (
    <>
      <Helmet>
        <title> Marketplace Packs </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Marketplace Packs
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:swap-outline" />}
              onClick={() => setConnectionDialogOpen(true)}
            >
              Migrate Data
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-outline" />}
              onClick={() => navigate(withTenant(`/dashboard/marketplace-packs/pack`))}
            >
              Create Pack
            </Button>
          </Stack>
        </Stack>

        <Card>
          {rows === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Publish Date</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.name}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.publishDate}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.expiryDate}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.quantity}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.price}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <img
                            style={{
                              margin: "0 auto",
                              width: 100,
                              background: "grey",
                            }}
                            alt="Pack"
                            src={row.image}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={handleOpenMenu}
                            id={row.marketplacePackId}
                          >
                            <Iconify icon={"eva:more-vertical-fill"} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
      {!isViewer && (
        <Popover
          open={Boolean(open)}
          anchorEl={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              p: 1,
              width: 140,
              "& .MuiMenuItem-root": {
                px: 1,
                typography: "body2",
                borderRadius: 0.75,
              },
            },
          }}
        >
          <MenuItem
            onClick={() =>
              navigate(withTenant(`/dashboard/marketplace-packs/pack?pack_id=${openId}`))
            }
          >
            <Iconify icon={"eva:edit-outline"} sx={{ mr: 2 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={() => handleDeletePack(openId)}>
            <Iconify icon={"eva:trash-2-outline"} sx={{ mr: 2 }} />
            Delete
          </MenuItem>
        </Popover>
      )}

      {openStageDialog && (
        <StageDialog
          stage={stage}
          open={openStageDialog}
          onSuccessHandler={handleSuccessStageDialog}
          onClose={handleCloseStageDialog}
        />
      )}

      <ConnectionMigrateDialog
        open={connectionDialogOpen}
        onClose={() => setConnectionDialogOpen(false)}
        title="Marketplace Packs Data Migration"
        description="Copies marketplace packs for the source tenant from the current database into the target tenant on the database below. Packs that already exist for the target tenant are skipped."
        apiEndpoint="/admin/marketplace/packs/migration"
        entityName="marketplace packs"
        showSourceTenant
        showSeason={false}
        initialSourceTenantId={searchParams.get("tenantId") || ""}
      />
    </>
  );
}
